import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Compass, LayoutDashboard, Bookmark, Plus, Shield, Menu, X, LogIn,
  Globe, Settings, Sparkles, ChevronDown, FolderOpen, ShieldCheck,
  Info, User, LogOut, MapPin, Languages, DollarSign, Route, BarChart3, TrendingUp,
  ArrowLeft, Link2, ListChecks, GitMerge, Handshake, BookOpen, FileText, ScrollText,
} from 'lucide-react';
import { useGlobalSettings } from '@/context/GlobalSettingsContext';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();
  const { user, refreshAuth } = useAuth();
  const {
    language, setLanguage, currency, setCurrency,
    country, setCountry,
    LANGUAGES, CURRENCIES, COUNTRIES,
  } = useGlobalSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [globeOpen, setGlobeOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const profileRef = useRef(null);
  const globeRef = useRef(null);
  const aboutRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (globeRef.current && !globeRef.current.contains(e.target)) setGlobeOpen(false);
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape for accessibility
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAboutOpen(false);
        setGlobeOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close mobile nav on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const currentLang = LANGUAGES.find(l => l.code === language);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem('hustlehub_session');
    await refreshAuth();
    setProfileOpen(false);
    navigate('/');
  };

  // Primary nav items (always visible)
  const PRIMARY_NAV = [
    { path: '/browse', label: t('nav.browse'), icon: Compass },
    { path: '/trending', label: 'Trending', icon: TrendingUp },
    { path: '/match', label: 'Quick Match', icon: Sparkles },
    { path: '/top-rated', label: 'Top Rated', icon: ShieldCheck },
  ];

  // Secondary nav items (logged-in)
  const USER_NAV = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
  ];

  const ALL_NAV = [
    ...PRIMARY_NAV,
    ...(user ? USER_NAV : []),
  ];

  return (
    <>
      <header className="sticky top-0 z-50 glass-light border-b border-slate-100/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="header">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-[60px]">
            {/* ═══ BACK + LOGO ═══ */}
            <div className="flex items-center gap-1.5 shrink-0">
              {location.pathname !== '/' && (
                <button
                  onClick={() => navigate(-1)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all"
                  data-testid="header-back-button"
                  aria-label="Go back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo">
              <div className="w-10 h-10 flex-shrink-0">
                <img src="/logo192.png" alt="HustleHub AI" className="w-full h-full object-contain" width="40" height="40" />
              </div>
              <span className="text-[17px] font-bold text-slate-900 tracking-[-0.02em]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                HustleHub<span className="text-teal-500 font-extrabold">.ai</span>
              </span>
            </Link>
            </div>

            {/* ═══ CENTER NAV ═══ */}
            <nav className="hidden lg:flex items-center gap-0.5 bg-slate-50/80 rounded-full px-1 py-1 border border-slate-100/80" data-testid="desktop-nav" aria-label="Main navigation">
              {ALL_NAV.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path + label}
                  to={path}
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive(path.split('?')[0])
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </Link>
              ))}
              {user && (
                <Link
                  to="/my-analytics"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/my-analytics')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-my-analytics"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  My Analytics
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/admin')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-admin"
                >
                  <Shield className="h-3.5 w-3.5" />
                  {t('nav.admin')}
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/growth"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/growth')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-growth"
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  Growth
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/integrity"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/integrity')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-integrity"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Integrity
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin/analytics"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/admin/analytics')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-admin-analytics"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Analytics
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/affiliate"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/affiliate')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-affiliate"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Affiliate
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/data-quality"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/data-quality')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-data-quality"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Quality
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin/url-health"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/admin/url-health')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-admin-url-health"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  URL Health
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin/url-proposals"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 ${
                    isActive('/admin/url-proposals')
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/60'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'
                  }`}
                  data-testid="nav-admin-url-proposals"
                >
                  <ListChecks className="h-3.5 w-3.5" />
                  URL Queue
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link
                  to="/admin/phase7-corrections"
                  className={`flex items-center gap-1.5 px-4 py-[7px] rounded-full text-[13px] font-medium transition-all duration-200 border ${
                    isActive('/admin/phase7-corrections')
                      ? 'bg-teal-600 text-white border-teal-700 shadow-sm'
                      : 'bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100'
                  }`}
                  title="Review narrowed-listing correction candidates. Decisions are staging-only; live changes require the audited apply CLI."
                  data-testid="nav-admin-phase7-corrections"
                >
                  <GitMerge className="h-3.5 w-3.5" />
                  Phase 7.5
                  <span
                    className={`ml-0.5 inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-semibold uppercase tracking-wide ${
                      isActive('/admin/phase7-corrections')
                        ? 'bg-white/20 text-white'
                        : 'bg-teal-200/70 text-teal-900'
                    }`}
                  >
                    staging
                  </span>
                </Link>
              )}
            </nav>

            {/* ═══ RIGHT CONTROLS ═══ */}
            <div className="flex items-center gap-1.5">
              {/* Desktop-only "About" dropdown — gathers trust/editorial pages
                  for E-E-A-T transparency without cluttering the main nav. */}
              <div className="relative hidden lg:block" ref={aboutRef}>
                <button
                  onClick={() => { setAboutOpen(!aboutOpen); setGlobeOpen(false); setProfileOpen(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && !aboutOpen) {
                      e.preventDefault();
                      setAboutOpen(true);
                    }
                  }}
                  className={`flex items-center gap-1 px-2.5 py-[7px] rounded-xl text-[12px] font-medium transition-all duration-200 ${
                    aboutOpen
                      ? 'bg-slate-100 text-slate-800 border border-slate-200 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent'
                  }`}
                  data-testid="header-about-trigger"
                  aria-haspopup="menu"
                  aria-expanded={aboutOpen}
                  aria-label="About HustleHub menu"
                >
                  About
                  <ChevronDown className={`h-3 w-3 transition-transform ${aboutOpen ? 'rotate-180' : ''}`} />
                </button>

                {aboutOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl border border-slate-200/80 shadow-[0_20px_60px_rgba(15,23,42,0.12),0_8px_16px_rgba(15,23,42,0.06)] z-50 overflow-hidden animate-slide-down"
                    role="menu"
                    aria-label="About HustleHub"
                    data-testid="header-about-dropdown"
                  >
                    <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">About HustleHub</p>
                    </div>
                    <div className="py-1.5">
                      {[
                        { path: '/about', label: 'About us', icon: Info },
                        { path: '/how-we-evaluate', label: 'How we evaluate', icon: ShieldCheck },
                        { path: '/trust-safety', label: 'Trust & safety', icon: Shield },
                        { path: '/editorial-policy', label: 'Editorial policy', icon: ScrollText },
                        { path: '/editorial-standards', label: 'Editorial standards', icon: BookOpen },
                        { path: '/affiliate-disclosure', label: 'Affiliate disclosure', icon: FileText },
                        { path: '/partner-with-us', label: 'Partner with us', icon: Handshake },
                      ].map(item => (
                        <Link
                          key={item.path}
                          to={item.path}
                          role="menuitem"
                          onClick={() => setAboutOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:bg-slate-50 focus:text-slate-900 focus:outline-none transition-colors"
                          data-testid={`header-about-${item.path.replace(/^\//, '')}`}
                        >
                          <item.icon className="h-3.5 w-3.5 text-slate-400" />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Subtle public CTA — affiliate networks, sponsors, partners */}
              <Link
                to="/partner-with-us"
                className="hidden lg:inline-flex items-center px-2.5 py-[7px] rounded-xl text-[12px] font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-50 border border-transparent transition-all"
                data-testid="header-partner-with-us-link"
              >
                Partner with us
              </Link>

              {/* Globe / Locale Selector — visible to everyone */}
              <div className="relative" ref={globeRef}>
                <button
                  onClick={() => { setGlobeOpen(!globeOpen); setProfileOpen(false); }}
                  className={`flex items-center gap-1.5 px-2.5 py-[7px] rounded-xl text-[12px] font-medium transition-all duration-200 ${
                    globeOpen
                      ? 'bg-teal-50 text-teal-700 border border-teal-200 shadow-sm'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 border border-transparent'
                  }`}
                  data-testid="globe-selector"
                  aria-label="Language, currency and region"
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{currentLang?.code?.toUpperCase()}</span>
                  <span className="hidden sm:inline text-slate-200">|</span>
                  <span className="hidden sm:inline">{currency}</span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${globeOpen ? 'rotate-180' : ''}`} />
                </button>

                {globeOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200/80 shadow-[0_20px_60px_rgba(15,23,42,0.12),0_8px_16px_rgba(15,23,42,0.06)] z-50 overflow-hidden animate-slide-down" data-testid="globe-dropdown">
                    <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Global Settings</p>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1">
                          <Languages className="h-3 w-3" /> {t('footer.language')}
                        </label>
                        <Select value={language} onValueChange={setLanguage}>
                          <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="header-language-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1">
                          <DollarSign className="h-3 w-3" /> {t('footer.currency')}
                        </label>
                        <Select value={currency} onValueChange={setCurrency}>
                          <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="header-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> Country / Region
                        </label>
                        <Select value={country} onValueChange={setCountry}>
                          <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="header-country-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-52">
                            {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100">
                      <button
                        onClick={() => { setGlobeOpen(false); navigate('/settings'); }}
                        className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold text-teal-600 hover:text-teal-700 transition-colors"
                        data-testid="header-settings-link"
                      >
                        <Settings className="h-3.5 w-3.5" /> All Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Controls */}
              {user ? (
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => { setProfileOpen(!profileOpen); setGlobeOpen(false); }}
                    className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-xl transition-all duration-200 ${
                      profileOpen
                        ? 'bg-slate-100 border border-slate-200 shadow-sm'
                        : 'hover:bg-slate-50 border border-transparent'
                    }`}
                    data-testid="user-avatar-btn"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-[11px] font-bold shadow-sm">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden sm:block text-[13px] font-medium text-slate-700 max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {profileOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-slate-200/80 shadow-[0_20px_60px_rgba(15,23,42,0.12),0_8px_16px_rgba(15,23,42,0.06)] z-50 overflow-hidden animate-slide-down" data-testid="profile-dropdown">
                      <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100">
                        <p className="text-[13px] font-semibold text-slate-800 truncate">{user.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
                      </div>
                      <div className="py-1.5">
                        {[
                          { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
                          { path: '/saved', label: t('nav.saved'), icon: Bookmark },
                          { path: '/submit', label: t('nav.submit'), icon: Plus },
                          { path: '/settings', label: t('nav.settings'), icon: Settings },
                        ].map(item => (
                          <button
                            key={item.path}
                            onClick={() => { setProfileOpen(false); navigate(item.path); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                            data-testid={`profile-${item.label.toLowerCase()}`}
                          >
                            <item.icon className="h-3.5 w-3.5 text-slate-400" />
                            {item.label}
                          </button>
                        ))}
                      </div>
                      <div className="py-1.5 border-t border-slate-100">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] text-red-500 hover:bg-red-50 transition-colors"
                          data-testid="profile-logout"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="hidden sm:flex bg-slate-900 text-white hover:bg-slate-800 rounded-xl px-5 h-9 text-[13px] font-semibold shadow-sm"
                  data-testid="login-button"
                >
                  <LogIn className="h-3.5 w-3.5 mr-1.5" />
                  {t('nav.signIn')}
                </Button>
              )}

              {/* Mobile toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-all"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ MOBILE SLIDE-OUT NAV ═══ */}
      {mobileOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />

          {/* Panel */}
          <div className="fixed right-0 top-0 bottom-0 w-[300px] bg-white z-50 lg:hidden shadow-[-8px_0_30px_rgba(0,0,0,0.08)] flex flex-col" data-testid="mobile-nav-panel">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 h-[60px] border-b border-slate-100 shrink-0">
              <span className="text-[15px] font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                HustleHub<span className="text-teal-500">.ai</span>
              </span>
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-800 transition-all" data-testid="mobile-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* User info */}
            {user && (
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-slate-900 truncate">{user.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto px-3 py-4">
              <p className="px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Discover</p>
              {[
                { path: '/browse', label: t('nav.browse'), icon: Compass },
                { path: '/trending', label: 'Trending', icon: TrendingUp },
                { path: '/new', label: 'New', icon: Sparkles },
                { path: '/top-rated', label: 'Top Rated', icon: ShieldCheck },
                { path: '/match', label: 'Quick Match', icon: Sparkles },
                { path: '/browse?view=categories', label: 'Categories', icon: FolderOpen },
              ].map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                    isActive(item.path.split('?')[0])
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`mobile-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}

              {user && (
                <>
                  <p className="px-3 mt-5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Your Hub</p>
                  {[
                    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
                    { path: '/saved', label: t('nav.saved'), icon: Bookmark },
                    { path: '/submit', label: t('nav.submit'), icon: Plus },
                  ].map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                        isActive(item.path)
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                      data-testid={`mobile-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  ))}
                </>
              )}

              {user && (
                <>
                  <p className="px-3 mt-5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Analytics</p>
                  <Link
                    to="/my-analytics"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/my-analytics') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-my-analytics"
                  >
                    <BarChart3 className="h-4 w-4" /> My Analytics
                  </Link>
                </>
              )}

              {user?.role === 'admin' && (
                <>
                  <p className="px-3 mt-5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Admin</p>
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/admin') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-admin"
                  >
                    <Shield className="h-4 w-4" /> {t('nav.admin')}
                  </Link>
                  <Link
                    to="/growth"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/growth') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-growth"
                  >
                    <TrendingUp className="h-4 w-4" /> Growth Engine
                  </Link>
                  <Link
                    to="/integrity"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/integrity') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-integrity"
                  >
                    <ShieldCheck className="h-4 w-4" /> Data Integrity
                  </Link>
                  <Link
                    to="/affiliate"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/affiliate') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-affiliate"
                  >
                    <BarChart3 className="h-4 w-4" /> Affiliate
                  </Link>
                  <Link
                    to="/admin/analytics"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/admin/analytics') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-admin-analytics"
                  >
                    <BarChart3 className="h-4 w-4" /> Analytics
                  </Link>
                  <Link
                    to="/data-quality"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/data-quality') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-data-quality"
                  >
                    <ShieldCheck className="h-4 w-4" /> Data Quality
                  </Link>
                  <Link
                    to="/admin/url-health"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/admin/url-health') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-admin-url-health"
                  >
                    <Link2 className="h-4 w-4" /> URL Health
                  </Link>
                  <Link
                    to="/admin/url-proposals"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/admin/url-proposals') ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="mobile-admin-url-proposals"
                  >
                    <ListChecks className="h-4 w-4" /> URL Queue
                  </Link>
                  <Link
                    to="/admin/phase7-corrections"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                      isActive('/admin/phase7-corrections')
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'bg-teal-50 text-teal-800 hover:bg-teal-100'
                    }`}
                    data-testid="mobile-admin-phase7-corrections"
                  >
                    <span className="flex items-center gap-3">
                      <GitMerge className="h-4 w-4" /> Phase 7.5 Corrections
                    </span>
                    <span
                      className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-px rounded-full ${
                        isActive('/admin/phase7-corrections')
                          ? 'bg-white/20 text-white'
                          : 'bg-teal-200/70 text-teal-900'
                      }`}
                    >
                      staging
                    </span>
                  </Link>
                </>
              )}

              {/* About / Trust links — visible to all users for E-E-A-T transparency */}
              <p className="px-3 mt-5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">About HustleHub</p>
              {[
                { path: '/about', label: 'About us', icon: Info },
                { path: '/how-we-evaluate', label: 'How we evaluate', icon: ShieldCheck },
                { path: '/trust-safety', label: 'Trust & safety', icon: Shield },
                { path: '/editorial-policy', label: 'Editorial policy', icon: ScrollText },
                { path: '/editorial-standards', label: 'Editorial standards', icon: BookOpen },
                { path: '/affiliate-disclosure', label: 'Affiliate disclosure', icon: FileText },
                { path: '/partner-with-us', label: 'Partner with us', icon: Handshake },
              ].map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium mb-0.5 transition-all ${
                    isActive(item.path)
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`mobile-${item.path.replace(/^\//, '').replace(/\//g, '-')}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}

              {/* Global Settings in Mobile */}
              <p className="px-3 mt-5 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Region & Language</p>
              <div className="px-3 space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
                    <Languages className="h-2.5 w-2.5" /> {t('footer.language')}
                  </label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="mobile-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
                    <DollarSign className="h-2.5 w-2.5" /> {t('footer.currency')}
                  </label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="mobile-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1">
                    <MapPin className="h-2.5 w-2.5" /> Country
                  </label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="h-9 text-[13px] rounded-xl" data-testid="mobile-country-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="max-h-48">
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Panel footer */}
            <div className="px-5 py-4 border-t border-slate-100 shrink-0 space-y-2">
              <Link
                to="/partner-with-us"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 text-[13px] text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="mobile-partner-with-us-link"
              >
                <Handshake className="h-4 w-4" /> Partner with us
              </Link>
              <Link
                to="/settings"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 text-[13px] text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="mobile-settings-link"
              >
                <Settings className="h-4 w-4" /> {t('nav.settings')}
              </Link>
              {!user && (
                <Button
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                  className="w-full bg-slate-900 text-white rounded-xl h-10 text-[13px] font-semibold mt-2"
                  data-testid="mobile-login-button"
                >
                  <LogIn className="h-4 w-4 mr-2" /> {t('nav.signIn')}
                </Button>
              )}
              {user && (
                <button
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="flex items-center gap-2.5 text-[13px] text-red-500 hover:text-red-600 transition-colors"
                  data-testid="mobile-logout"
                >
                  <LogOut className="h-4 w-4" /> Sign Out
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
